"use client";

import { useEffect, useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SITE } from "@/lib/constants";
import { useFocusTrap } from "@/lib/use-focus-trap";
import { capture } from "@/lib/posthog-browser";

/**
 * Premium paywall modal (V2 §6.1).
 *
 * Context-aware messaging based on trigger:
 * - "planner" — triggered from Add to Planner
 * - "save_limit" — triggered when reaching 5-camp save limit
 * - "smart_filter" — triggered from smart filter click
 * - "generic" — default
 *
 * Two-column Free vs. Premium comparison.
 * CTA wires to /api/stripe/checkout.
 */

type PaywallTrigger = "planner" | "save_limit" | "smart_filter" | "generic";

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  trigger?: PaywallTrigger;
  onCheckout?: () => void;
}

const TRIGGER_HEADLINES: Record<PaywallTrigger, string> = {
  planner:
    "Plan the perfect summer with Wildroost Premium",
  save_limit:
    "You've saved 5 camps. Unlock unlimited saves",
  smart_filter:
    "Smart Filters help you find the right camp faster",
  generic:
    "Your family. Your activities. One place.",
};

const TRIGGER_SUBTEXTS: Record<PaywallTrigger, string> = {
  planner:
    "The planner helps you map out every week, track budgets, and share with your co-parent.",
  save_limit:
    "Free accounts can save up to 5 camps. Premium gives you unlimited saves plus the full planner.",
  smart_filter:
    "Sibling Overlap, Gap Finder, Budget Aware, and more: filters that actually understand your family.",
  generic:
    "Kid profiles, smart filters, and a planner that keeps your whole summer organized.",
};

export function PaywallModal({
  isOpen,
  onClose,
  trigger = "generic",
  onCheckout,
}: PaywallModalProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  const firedRef = useRef(false);
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
      if (!firedRef.current) {
        firedRef.current = true;
        try { capture("paywall_shown", { trigger }); } catch {}
      }
    } else {
      firedRef.current = false;
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown, trigger]);

  const trapRef = useFocusTrap<HTMLDivElement>(isOpen);

  // Portal the overlay to <body> so its `fixed inset-0` anchors to the viewport
  // rather than the nearest containing block. The TopBar <header> sets
  // `backdrop-blur-[20px]` (backdrop-filter) which — like transform/filter —
  // makes itself the containing block for fixed-position descendants. This
  // modal is rendered inside that header (via AvatarDropdown), so without the
  // portal the overlay anchors to the 64px header and the vertically-centered
  // card is clipped above the top of the screen (#1155). A height cap alone
  // can't fix that — the containing block, not the card height, is the bug.
  // Mirrors MobileNav / date-add-popover, which portal for the same reason.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see comment above effect
    setMounted(true);
  }, []);

  if (!isOpen || !mounted) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Upgrade to Premium"
    >
      <div
        ref={trapRef}
        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-text-tertiary transition-colors hover:bg-cloud hover:text-text-primary"
          aria-label="Close"
        >
          &times;
        </button>

        {/* Header */}
        <div className="border-b border-cloud px-8 pb-6 pt-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-wildroost-gold/15">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-wildroost-gold">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2 className="font-display text-xl font-bold text-text-primary">
            {TRIGGER_HEADLINES[trigger]}
          </h2>
          <p className="mt-2 text-sm text-text-secondary">
            {TRIGGER_SUBTEXTS[trigger]}
          </p>
        </div>

        {/* Free vs. Premium comparison */}
        <div className="grid grid-cols-2 gap-0 divide-x divide-cloud border-b border-cloud">
          {/* Free column */}
          <div className="p-6">
            <h3 className="mb-4 text-center text-sm font-bold text-text-tertiary">
              Free
            </h3>
            <ul className="space-y-3 text-sm text-text-secondary">
              <ComparisonItem included>
                Browse all {SITE.activityLabelPlural}
              </ComparisonItem>
              <ComparisonItem included>Read reviews</ComparisonItem>
              <ComparisonItem included>Save up to 5</ComparisonItem>
              <ComparisonItem included>Registration alerts</ComparisonItem>
              <ComparisonItem>Smart filters</ComparisonItem>
              <ComparisonItem>Planner access</ComparisonItem>
              <ComparisonItem>Budget tracking</ComparisonItem>
              <ComparisonItem>Co-parent sharing</ComparisonItem>
              <ComparisonItem>Calendar export</ComparisonItem>
            </ul>
          </div>

          {/* Premium column */}
          <div className="relative bg-gold-light/50 p-6">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-wildroost-gold px-3 py-0.5 text-[10px] font-bold text-white shadow-sm">
              Best Value
            </span>
            <h3 className="mb-4 text-center text-sm font-bold text-wildroost-gold">
              Premium
            </h3>
            <ul className="space-y-3 text-sm text-text-primary">
              <ComparisonItem included>
                Browse all {SITE.activityLabelPlural}
              </ComparisonItem>
              <ComparisonItem included>Read & write reviews</ComparisonItem>
              <ComparisonItem included>Unlimited saves</ComparisonItem>
              <ComparisonItem included>Registration alerts</ComparisonItem>
              <ComparisonItem included premium>
                Smart filters
              </ComparisonItem>
              <ComparisonItem included premium>
                Full planner
              </ComparisonItem>
              <ComparisonItem included premium>
                Budget tracking
              </ComparisonItem>
              <ComparisonItem included premium>
                Co-parent sharing
              </ComparisonItem>
              <ComparisonItem included premium>
                Calendar export
              </ComparisonItem>
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="p-8 text-center">
          <button
            onClick={() => {
              try { capture("upgrade_clicked", { trigger }); } catch {}
              onCheckout?.();
            }}
            className="w-full rounded-xl bg-wildroost-gold py-4 font-display text-base font-bold text-white transition-colors hover:bg-[#c08530]"
          >
            Upgrade to Wildroost Premium
          </button>
          <p className="mt-3 text-sm text-text-tertiary">
            {SITE.premiumPriceDisplay}. One family, unlimited kids. Cancel
            anytime.
          </p>
          <button
            onClick={onClose}
            className="mt-2 text-sm font-medium text-text-tertiary transition-colors hover:text-text-secondary"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ComparisonItem({
  children,
  included = false,
  premium = false,
}: {
  children: React.ReactNode;
  included?: boolean;
  premium?: boolean;
}) {
  return (
    <li className="flex items-start gap-2">
      {included ? (
        <span
          className={`mt-0.5 text-xs ${premium ? "text-wildroost-gold" : "text-wildroost-teal"}`}
        >
          ✓
        </span>
      ) : (
        <span className="mt-0.5 text-xs text-text-tertiary">—</span>
      )}
      <span className={!included ? "text-text-tertiary" : ""}>
        {children}
      </span>
    </li>
  );
}
